import React, { useEffect, useState, useRef } from 'react'
import labelService, { LabelNode, LabelSetItem, LabelSetStatus, LabelSetSource, LabelSetMeta } from '../services/labelService'
import { PERMISSIONS } from '../constants/permissions'
import InfoModal from '../components/InfoModal'
import CreateLabelDataModal, { LabelFormData } from '../components/CreateLabelDataModal'
import LabelUploadModal from '../components/LabelUploadModal'
import LabelIngestProgressModal from '../components/LabelIngestProgressModal'
import config from '../config'
import { trackPackaging, trackedKeys } from '../services/packagingWatcher'

interface Props {
  userInfo: { email: string; fullName: string; isAdmin: boolean; permissions: string[] }
}

// 라벨 형식은 서버에서 자동 판별함

export default function LabelDataManagement({ userInfo }: Props) {
  const [nodes, setNodes] = useState<LabelNode[]>([])
  const [expanded, setExpanded] = useState<Set<number>>(new Set())
  const [selected, setSelected] = useState<LabelNode | null>(null)
  const [isCreateOpen, setCreateOpen] = useState(false)
  const [isUploadOpen, setUploadOpen] = useState(false)
  const [uploading, setUploading] = useState(false)
  const [status, setStatus] = useState<LabelSetStatus | null>(null)
  const [items, setItems] = useState<LabelSetItem[]>([])
  const [tab, setTab] = useState<'matched' | 'unmatched' | 'duplicate'>('matched')
  const [deferList, setDeferList] = useState<boolean>(false)
  const [childCounts, setChildCounts] = useState<Record<number, number>>({})
  const [leafLabelCounts, setLeafLabelCounts] = useState<Record<number, number>>({})
  const [isProgressOpen, setProgressOpen] = useState(false)
  const [ingestStep, setIngestStep] = useState<'idle' | 'uploading' | 'queued' | 'processing' | 'completed' | 'failed'>('idle')
  const [progressStatus, setProgressStatus] = useState<LabelSetStatus | null>(null)
  const [sources, setSources] = useState<LabelSetSource[] | null>(null)
  const [meta, setMeta] = useState<LabelSetMeta | null>(null)
  const PAGE_SIZE = 50
  const [labelSkip, setLabelSkip] = useState<number>(0)
  const [labelHasMore, setLabelHasMore] = useState<boolean>(false)
  const [isRefBlockedOpen, setRefBlockedOpen] = useState<boolean>(false)
  const [refBlockedMessage, setRefBlockedMessage] = useState<string>('')
  const [downloadingVerId, setDownloadingVerId] = useState<number | null>(null)
  const [packagingVerIds, setPackagingVerIds] = useState<Set<number>>(new Set())
  // 버전별 패키징 진행률 표시 문자열 (예: "43%")
  const [packagingProgress, setPackagingProgress] = useState<Map<number, string>>(new Map())
  const [deletingLabelVersionId, setDeletingLabelVersionId] = useState<number | null>(null)
  const packagingIntervalsRef = useRef<Map<number, NodeJS.Timeout>>(new Map())

  const canManageLabelProjectGlobal =
    !!userInfo?.isAdmin || (userInfo?.permissions || []).includes(PERMISSIONS.LABEL_PROJECT_MANAGE)

  // 원천데이터와 동일하게, 라벨 프로젝트 루트 중 하나라도 actions.create 가 있으면
  // 구조 생성 버튼을 노출한다.
  const hasAnyCreateLabelPermission = (() => {
    if (!nodes || nodes.length === 0) return false
    return nodes.some(n => {
      const isRoot = n.parent_id == null
      const actions = (n as any).actions as Record<string, boolean> | undefined
      return isRoot && !!actions?.create
    })
  })()

  const canShowCreateButton = canManageLabelProjectGlobal || hasAnyCreateLabelPermission

  const loadFirstItems = async (nodeId: number, which: 'matched' | 'unmatched' | 'duplicate') => {
    const list = await labelService.listLabelSetItems(nodeId, which, 0, PAGE_SIZE)
    setItems(list)
    setLabelSkip(list.length)
    setLabelHasMore(list.length === PAGE_SIZE)
  }

  const loadMoreItems = async () => {
    if (!selected) return
    const more = await labelService.listLabelSetItems(selected.id, tab, labelSkip, PAGE_SIZE)
    setItems(prev => [...prev, ...more])
    setLabelSkip(prev => prev + more.length)
    if (more.length < PAGE_SIZE) setLabelHasMore(false)
  }

  const [descExpanded, setDescExpanded] = useState<boolean>(false)

  useEffect(() => { loadRoots() }, [])

  // 컴포넌트 언마운트시 패키징 폴링 정리
  useEffect(() => {
    return () => {
      packagingIntervalsRef.current.forEach(interval => clearInterval(interval))
      packagingIntervalsRef.current.clear()
    }
  }, [])

  // 패키징 감시(폴링·완료 알림·자동 다운로드)는 전역 packagingWatcher 가 담당한다.
  // 여기서는 그 이벤트를 받아 버튼 상태·진행률 표기만 갱신한다.
  const verIdOfKey = (key?: string): number | null => {
    if (typeof key !== 'string' || !key.startsWith('label:')) return null
    const id = Number(key.slice(6))
    return Number.isFinite(id) ? id : null
  }
  useEffect(() => {
    const tracked = trackedKeys('label:')
      .map(k => Number(k.slice(6)))
      .filter(n => Number.isFinite(n))
    if (tracked.length) {
      setPackagingVerIds(prev => {
        const s = new Set(prev)
        tracked.forEach(id => s.add(id))
        return s
      })
    }
    const onProgress = (e: Event) => {
      const { key, progress, packedBytes } = ((e as CustomEvent).detail || {}) as {
        key?: string; progress?: number; packedBytes?: number
      }
      const verId = verIdOfKey(key)
      if (verId == null) return
      setPackagingVerIds(prev => (prev.has(verId) ? prev : new Set(prev).add(verId)))
      const label = typeof progress === 'number'
        ? `${progress}%`
        : (typeof packedBytes === 'number' ? `${Math.round(packedBytes / 1048576)}MB` : '')
      if (label) setPackagingProgress(prev => new Map(prev).set(verId, label))
    }
    const onDone = (e: Event) => {
      const { key } = ((e as CustomEvent).detail || {}) as { key?: string }
      const verId = verIdOfKey(key)
      if (verId == null) return
      setPackagingVerIds(prev => { const s = new Set(prev); s.delete(verId); return s })
      setPackagingProgress(prev => { const m = new Map(prev); m.delete(verId); return m })
    }
    window.addEventListener('packaging:progress', onProgress)
    window.addEventListener('packaging:done', onDone)
    return () => {
      window.removeEventListener('packaging:progress', onProgress)
      window.removeEventListener('packaging:done', onDone)
    }
  }, [])

  // 라벨셋 삭제 중에는 전체 페이지의 마우스 커서를 로딩 상태로 표시
  useEffect(() => {
    if (deletingLabelVersionId !== null) {
      const prev = document.body.style.cursor
      document.body.style.cursor = 'wait'
      return () => {
        document.body.style.cursor = prev
      }
    }
  }, [deletingLabelVersionId])

  const loadRoots = async () => {
    const roots = await labelService.getRootNodes()
    setNodes(roots)
  }

  const toggleNode = async (node: LabelNode) => {
    const ns = new Set(expanded)
    if (ns.has(node.id)) ns.delete(node.id)
    else {
      ns.add(node.id)
      const children = await labelService.getNodeChildren(node.id)
      // 자식 수 기록
      setChildCounts(prev => ({ ...prev, [node.id]: children.length }))
      setNodes(prev => {
        const ids = new Set(prev.map(n => n.id))
        const merged = [...prev]
        children.forEach(c => { if (!ids.has(c.id)) merged.push(c) })
        return merged
      })
      // 바로 아래 하위폴더들의 자식 수도 선로드하여 클릭 전에도 표시
      ;(async () => {
        try {
          for (const c of children) {
            if (childCounts[c.id] == null) {
              const g = await labelService.getNodeChildren(c.id)
              setChildCounts(prev => ({ ...prev, [c.id]: g.length }))
            }
            // 버전(최하위) 노드면 라벨 총개수 미리 조회하여 표시 준비
            try {
              if (c.depth === 2) {
                const st = await labelService.getLabelSetStatus(c.id)
                setLeafLabelCounts(prev => ({ ...prev, [c.id]: st?.total ?? 0 }))
              }
            } catch {}
          }
        } catch {}
      })()
    }
    setExpanded(ns)
  }

  // 좌측/우측에서 노드를 선택했을 때 공통 처리: 선택, 부모 경로 확장, 자식 로드/확장
  const handleNodeSelect = async (node: LabelNode) => {
    const same = selected?.id === node.id
    if (!same) {
      setSelected(node)
      setDescExpanded(false)
      setSources(null)
    }
    // 부모 체인 자동 확장 (현재 보유한 nodes에서 추적)
    try {
      const parentIds: number[] = []
      let cur: LabelNode | undefined = nodes.find(n => n.id === node.id)
      while (cur?.parent_id) {
        parentIds.unshift(cur.parent_id)
        cur = nodes.find(n => n.id === cur!.parent_id)
      }
      if (parentIds.length > 0) {
        setExpanded(prev => {
          const ns = new Set(prev)
          parentIds.forEach(pid => ns.add(pid))
          return ns
        })
      }
    } catch {}
    // 자신의 자식도 바로 로드/확장하여 우측에 하위 항목이 연쇄적으로 표시되도록
    if (!expanded.has(node.id)) {
      await toggleNode(node)
    }
    // 같은 노드를 재클릭한 경우에도 정보 갱신
    if (same) {
      await loadStatusAndItems(node)
    }
  }

  const handleCreate = async (data: LabelFormData) => {
    // 새 버전 노드 반환
    const created = await labelService.createLabelProject({ name: data.name, group: data.group, version: data.version, projectDescription: data.projectDescription, groupDescription: data.groupDescription, versionDescription: data.versionDescription, existingProjectId: data.existingProjectId })
    // 루트 재로드 및 확장/선택 경로 구성
    const roots = await labelService.getRootNodes()
    setNodes(roots)
    // 확장 상태 초기화 (기존 잔존 상태로 인해 클릭 무반응 방지)
    setExpanded(new Set())
    setSelected(null)
    setStatus(null)
    setItems([])
    setTab('matched')
    setDeferList(true)

    // created.path: "/<root>/<group>/<version>/" 형식 가정 → id 목록 추출
    const ids = (created?.path || '')
      .split('/')
      .filter(Boolean)
      .map((s) => Number(s))
    if (ids.length === 3) {
      const [rootId, groupId, versionId] = ids
      try {
        // 루트 확장
        const rootChildren = await labelService.getNodeChildren(rootId)
        setChildCounts((prev) => ({ ...prev, [rootId]: rootChildren.length }))
        setNodes((prev) => {
          const map = new Map(prev.map((n) => [n.id, n]))
          rootChildren.forEach((c) => map.set(c.id, c))
          return Array.from(map.values())
        })
        setExpanded((prev) => {
          const ns = new Set(prev)
          ns.add(rootId)
          return ns
        })
        // 그룹 확장
        const groupChildren = await labelService.getNodeChildren(groupId)
        setChildCounts((prev) => ({ ...prev, [groupId]: groupChildren.length }))
        setNodes((prev) => {
          const map = new Map(prev.map((n) => [n.id, n]))
          groupChildren.forEach((c) => map.set(c.id, c))
          return Array.from(map.values())
        })
        setExpanded((prev) => {
          const ns = new Set(prev)
          ns.add(groupId)
          return ns
        })
        // 버전 선택
        const ver = [...groupChildren, ...rootChildren, ...roots].find((n) => n.id === versionId) || created
        setSelected(ver)

        // 버전(리프) 노드들의 라벨 총개수 선반영 (없으면 0 표시)
        try {
          for (const v of groupChildren) {
            if (v.depth === 2) {
              try {
                const st = await labelService.getLabelSetStatus(v.id)
                setLeafLabelCounts(prev => ({ ...prev, [v.id]: st?.total ?? 0 }))
              } catch {
                setLeafLabelCounts(prev => ({ ...prev, [v.id]: 0 }))
              }
            }
          }
        } catch {}
      } catch {}
    }
    setCreateOpen(false)
  }

  const isVersionNode = (node: LabelNode | null): boolean => {
    if (!node) return false
    // depth 2 (PROJECT=0, GROUP=1, VERSION=2)로 가정
    return node.depth === 2
  }

  const loadStatusAndItems = async (node: LabelNode) => {
    const st = await labelService.getLabelSetStatus(node.id)
    setStatus(st)
    
    try {
      if (st && st.label_set_id) {
        const srcs = await labelService.listLabelSetSources(st.label_set_id)
        setSources(srcs)
        try { setMeta(await labelService.getLabelSetMeta(node.id)) } catch { setMeta(null) }
      } else {
        setSources(null)
        setMeta(null)
      }
    } catch { setSources(null) }
    if (st && st.status === 'completed') {
      // 사이드바 최하위(버전) 노드 라벨 총개수 갱신
      setLeafLabelCounts(prev => ({ ...prev, [node.id]: st.total }))
      if (!deferList) {
        await loadFirstItems(node.id, tab)
      } else {
        setItems([])
      }
    } else {
      setItems([])
    }
  }

  useEffect(() => {
    if (selected) loadStatusAndItems(selected)
  }, [selected, tab, deferList])

  // 업로드 후 상태가 processing이면 주기적으로 상태를 폴링하여 완료 시 갱신
  useEffect(() => {
    if (!selected) return
    if (status?.status !== 'processing') return
    let mounted = true
    const interval = setInterval(async () => {
      try {
        const st = await labelService.getLabelSetStatus(selected.id)
        if (!mounted) return
        if (st) {
          setStatus(st)
          setProgressStatus(st)
          if (st.status === 'completed' || st.status === 'failed') {
            setIngestStep(st.status as any)
            if (st.status === 'completed' || st.status === 'failed') {
              // 완료: 사이드바 카운트 갱신 및 기본 탭 초기화 + 즉시 소스/리스트 로드
              setLeafLabelCounts(prev => ({ ...prev, [selected.id]: st.total }))
              try {
                if (st.label_set_id) {
                  const srcs = await labelService.listLabelSetSources(st.label_set_id)
                  setSources(srcs)
                  try { setMeta(await labelService.getLabelSetMeta(selected.id)) } catch { setMeta(null) }
                }
              } catch {}
              if (st.status === 'completed') {
                setTab('matched')
                setDeferList(false)
                try { await loadFirstItems(selected.id, 'matched') } catch {}
              } else {
                // 실패 시 리스트는 초기화
                setItems([])
              }
            }
            // 진행 모달 닫기
            setTimeout(() => setProgressOpen(false), 300)
          }
        }
      } catch {}
    }, 2000)
    return () => { mounted = false; clearInterval(interval) }
  }, [selected, status?.status])

  // 노드 배열 중복 제거(같은 id의 노드가 중복으로 들어오는 경우 방지)
  useEffect(() => {
    if (!nodes || nodes.length === 0) return
    const map = new Map<number, LabelNode>()
    let changed = false
    for (const n of nodes) {
      if (!map.has(n.id)) map.set(n.id, n)
      else changed = true
    }
    if (changed || map.size !== nodes.length) {
      setNodes(Array.from(map.values()))
    }
  }, [nodes])

  // 현재 보이는 루트 노드들에 대해 자식 수 선로드 (클릭 전에도 개수 표기)
  useEffect(() => {
    const roots = nodes.filter(n => n.parent_id == null)
    if (roots.length === 0) return
    ;(async () => {
      for (const r of roots) {
        try {
          if (childCounts[r.id] == null) {
            const children = await labelService.getNodeChildren(r.id)
            setChildCounts(prev => ({ ...prev, [r.id]: children.length }))
            // 자식 노드를 미리 병합해도 트리에는 확장 전까지 보이지 않음
            setNodes(prev => {
              const ids = new Set(prev.map(n => n.id))
              const merged = [...prev]
              children.forEach(c => { if (!ids.has(c.id)) merged.push(c) })
              return merged
            })
            // 루트의 바로 아래 자식들에 대해서도 자식 수를 선로드
            for (const c of children) {
              if (childCounts[c.id] == null) {
                try {
                  const g = await labelService.getNodeChildren(c.id)
                  setChildCounts(prev => ({ ...prev, [c.id]: g.length }))
                  // 그룹의 자식(버전) 노드들의 라벨 총개수도 선로드
                  for (const v of g) {
                    try {
                      if (v.depth === 2) {
                        const st = await labelService.getLabelSetStatus(v.id)
                        setLeafLabelCounts(prev => ({ ...prev, [v.id]: st?.total ?? 0 }))
                      }
                    } catch {}
                  }
                } catch {}
              }
            }
          }
        } catch {}
      }
    })()
  }, [nodes])

  const renderNode = (node: LabelNode, level = 0) => {
    const isExpanded = expanded.has(node.id)
    const children = nodes.filter(n => n.parent_id === node.id)
    const mayHaveChildren = node.depth < 2
    const hasChildren = mayHaveChildren || children.length > 0
    return (
      <div key={node.id}>
        <div
          className={`flex items-center py-2 px-2 cursor-pointer rounded transition-colors ${
            selected?.id === node.id ? 'bg-gray-100 border-l-4 border-gray-800 text-gray-900' : 'hover:bg-gray-100'
          }`}
          style={{ paddingLeft: `${level * 20 + 12}px` }}
          onClick={async () => { await handleNodeSelect(node) }}
        >
          {/* 폴더 아이콘 (원천데이터관리 스타일) */}
          <svg 
            className="w-5 h-5 text-gray-700 mr-2 flex-shrink-0" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-5l-2-2H5a2 2 0 00-2 2z" />
          </svg>

          {/* 확장/축소 화살표 */}
          {(hasChildren) && (
            <button
              onClick={async (e) => { e.stopPropagation(); await toggleNode(node) }}
              className="mr-1 w-4 h-4 flex-shrink-0 flex items-center justify-center"
            >
              <svg 
                className={`w-4 h-4 text-gray-500 transition-transform ${isExpanded ? 'rotate-90' : ''}`} 
                fill="none" 
                stroke="currentColor" 
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 5l7 7-7 7" />
              </svg>
            </button>
          )}

          {/* 노드 이름 + 개수 (폴더: 자식 수, 버전(리프): 라벨 총개수) */}
          <span className="text-sm font-medium text-gray-900 min-w-0 break-all">
            {node.name}{(() => {
              if (node.depth === 2) {
                const total = leafLabelCounts[node.id]
                return total != null ? ` (${total})` : ''
              }
              const cnt = childCounts[node.id]
              const c = (cnt != null ? cnt : children.length)
              return c > 0 ? ` (${c})` : ''
            })()}
          </span>
        </div>
        {hasChildren && isExpanded && (
          <div>
            {children.map(c => renderNode(c, level + 1))}
          </div>
        )}
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col overflow-x-hidden">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900">라벨 데이터 관리</h1>
        <p className="text-gray-600">라벨 데이터 구조를 탐색하고 업로드/매칭 상태를 확인합니다.</p>
      </div>

      <div className="flex-1 flex border border-gray-200 rounded-lg overflow-hidden bg-white">
        {/* 좌측 트리 패널: 고정 폭으로 줄어들지 않도록 flex-none 적용 */}
        <div className="w-80 flex-none border-r border-gray-200 bg-gray-50">
          <div className="p-4 min-h-16 border-b border-gray-200 bg-white flex items-center justify-between">
            <h3 className="font-semibold text-gray-900">라벨 데이터 구조</h3>
            {canShowCreateButton && (
              <button
                onClick={() => setCreateOpen(true)}
                className="px-3 py-1 bg-gray-800 text-white text-xs rounded hover:bg-gray-900"
              >
                + 라벨데이터 생성
              </button>
            )}
          </div>
          <div className="p-2 overflow-y-auto" style={{ height: 'calc(100vh - 300px)' }}>
            {(() => {
              const roots = nodes.filter(n => n.parent_id == null)
              if (roots.length === 0) {
                return (
                  <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 350px)' }}>
                    <div className="text-center text-gray-500">
                      <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <p className="text-sm mb-2">등록된 라벨 데이터가 없습니다</p>
                      <p className="text-xs text-gray-400 mb-3">
                        {canShowCreateButton
                          ? '새로운 라벨 데이터를 생성해주세요'
                          : '관리자에게 라벨 데이터 구조 생성을 요청하세요.'}
                      </p>
                      {canShowCreateButton && (
                      <button 
                        onClick={() => setCreateOpen(true)}
                        className="px-3 py-1 bg-gray-800 text-white text-xs rounded hover:bg-gray-900"
                      >
                        라벨데이터 생성
                      </button>
                      )}
                    </div>
                  </div>
                )
              }
              return roots.map(r => renderNode(r))
            })()}
          </div>
        </div>

        {/* 우측 상세 패널: 내용이 길어도 좌측 패널 폭을 침범하지 않도록 min-w-0 지정 */}
        <div className="flex-1 bg-white min-w-0">
          <div className="p-4 min-h-16 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-900">{selected ? selected.name : '노드를 선택하세요'}</h3>
              {/* 실패 사유는 상태 표시 옆 가로 괄호로 표시되므로 별도 섹션은 제거 */}
            </div>
            {isVersionNode(selected) && (
              (!status || status.status === 'none') ? (
                <div className="flex space-x-2">
                  <label className="px-3 py-2 bg-gray-800 text-white text-sm rounded hover:bg-gray-900 cursor-pointer flex items-center">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                      <path d="M12 3v12"/>
                      <path d="m17 8-5-5-5 5"/>
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                    </svg>
                    <button onClick={() => setUploadOpen(true)}>라벨 업로드</button>
                  </label>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  {status.is_test_locked && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700 border border-red-300" title="테스트 데이터셋에 사용되어 학습 데이터셋으로 사용할 수 없습니다">
                      테스트 잠금
                    </span>
                  )}
                  <span className="text-sm text-gray-600">
                    상태: {status.status}
                    {status.status === 'failed' && (
                      <>
                        {' '}(
                        {(() => {
                          const r = status.fail_reason
                          if (r === 'user_abort') return '사용자 중단'
                          if (r === 'unsupported_format') return '형식 미지원'
                          if (r && String(r).trim().length > 0) return String(r)
                          return '알 수 없음'
                        })()}
                        )
                      </>
                    )}
                    {status.status === 'failed' && status.fail_message && (
                      <span className="block text-xs text-red-600 mt-0.5">{status.fail_message}</span>
                    )}
                  </span>
                  {status.status === 'processing' && (
                    <button
                      className="px-3 py-1 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50"
                      onClick={async () => {
                        try {
                          if (!status?.label_set_id) return
                          if (!window.confirm('진행 중인 라벨 처리 작업을 중단하시겠습니까?')) return
                          await labelService.abortLabelSet(status.label_set_id, 'user abort via UI')
                          // 중단 후 최신 상태를 재조회하여 fail_reason을 포함해 반영
                          try {
                            if (!selected) return
                            const st = await labelService.getLabelSetStatus(selected.id)
                            if (st) setStatus(st)
                            // 실패 시 메타/소스도 즉시 갱신
                            try {
                              if (st && st.label_set_id) {
                                const srcs = await labelService.listLabelSetSources(st.label_set_id)
                                setSources(srcs)
                                try { setMeta(await labelService.getLabelSetMeta(selected.id)) } catch { setMeta(null) }
                              }
                            } catch {}
                          } catch {}
                          setProgressOpen(false)
                        } catch (e: any) {
                          alert(e?.message || '중단에 실패했습니다.')
                        }
                      }}
                    >중단</button>
                  )}
                  {status.status === 'failed' && status.fail_reason !== 'unsupported_format' && (
                    <button
                      className="px-3 py-1 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50"
                      onClick={async () => {
                        try {
                          if (!status?.label_set_id) return
                          await labelService.retryLabelSet(status.label_set_id)
                          setStatus(prev => prev ? { ...prev, status: 'processing' } : prev)
                          setProgressOpen(true)
                          setIngestStep('processing')
                        } catch (e: any) {
                          alert(e?.message || '재시작에 실패했습니다. 라벨을 다시 업로드해 주세요.')
                        }
                      }}
                    >재시작</button>
                  )}
                  {status.status === 'completed' && (
                    <button
                      className={`px-3 py-1 text-sm border rounded ${downloadingVerId === selected?.id ? 'border-blue-300 bg-blue-50 text-blue-700 cursor-wait' : packagingVerIds.has(selected!.id) ? 'border-orange-300 bg-orange-50 text-orange-700 cursor-wait' : 'border-gray-300 bg-white'}`}
                      onClick={async () => {
                        if (!selected) return
                        if (downloadingVerId || packagingVerIds.has(selected.id)) return
                        try {
                          setDownloadingVerId(selected.id)
                          const token = localStorage.getItem('access_token')
                          const resp = await fetch(`${config.API_BASE_URL}/labels/sets/${selected.id}/download`, { headers: { 'Authorization': `Bearer ${token}` } as any })
                          if (resp.status === 202) {
                            const data = await resp.json()
                            setPackagingVerIds(prev => new Set(prev).add(selected.id))
                            // 전역 감시자에 등록 — 어느 화면에 있든 완료되면 알림 + 자동 다운로드
                            const exportPath = data.export_path || ''
                            const nameParts = [
                              data.node_label,
                              data.display_name || selected.name,
                            ].filter(Boolean)
                            trackPackaging(`label:${selected.id}`, {
                              statusUrl: `${config.API_BASE_URL}/files/downloads/status?export_path=${encodeURIComponent(exportPath)}`,
                              downloadUrl: `${config.API_BASE_URL}/labels/sets/${selected.id}/download${token ? `?access_token=${encodeURIComponent(token)}` : ''}`,
                              ts: Date.now(),
                              kind: '라벨데이터',
                              name: nameParts.join('\n'),
                            })
                            alert(`패키징을 시작했습니다. 완료되면 자동으로 다운로드됩니다.\n예상 크기: ${data.estimated_size_mb || '?'}MB (${data.file_count || '?'}개 파일)`)
                            return
                          }
                          if (!resp.ok) { alert(`다운로드 준비 실패: ${resp.status}`); return }
                          const blob = await resp.blob()
                          const cd = resp.headers.get('Content-Disposition') || ''
                          let filename = `labels_${selected.id}.tar.gz`
                          const m = /filename\*=UTF-8''([^;]+)|filename="?([^";]+)"?/i.exec(cd || '')
                          if (m) { filename = decodeURIComponent(m[1] || m[2] || filename) }
                          const url = URL.createObjectURL(blob)
                          const a = document.createElement('a')
                          a.href = url
                          a.download = filename
                          document.body.appendChild(a)
                          a.click()
                          a.remove()
                          URL.revokeObjectURL(url)
                        } catch (err) {
                          console.error('라벨 다운로드 실패', err)
                          alert('다운로드에 실패했습니다.')
                        } finally {
                          setDownloadingVerId(null)
                        }
                      }}
                    >{packagingVerIds.has(selected!.id)
                      ? `패키징 중…${packagingProgress.get(selected!.id) ? ` (${packagingProgress.get(selected!.id)})` : ''}`
                      : downloadingVerId === selected?.id ? '다운로드 중…' : '다운로드'}</button>
                  )}
                  {status.status === 'completed' && (
                    <button
                      className={`px-3 py-1 text-sm border border-gray-300 rounded ${
                        deletingLabelVersionId === selected?.id
                          ? 'bg-gray-100 text-gray-400 cursor-wait'
                          : 'hover:bg-gray-50'
                      }`}
                      disabled={deletingLabelVersionId !== null}
                      onClick={async () => {
                        if (!selected) return
                        if (!window.confirm('라벨 세트를 삭제하시겠습니까?\n삭제 후 재업로드할 수 있습니다.')) return
                        try {
                          setDeletingLabelVersionId(selected.id)
                          await labelService.deleteLabelSet(selected.id)
                          // 상태/목록 초기화
                          setStatus(null)
                          setItems([])
                          // 선택한 원천데이터 초기화
                          setSources(null)
                          // 라벨셋 메타 초기화
                          setMeta(null)
                          // 사이드바 버전 노드 총개수 즉시 0으로 반영
                          setLeafLabelCounts(prev => ({ ...prev, [selected.id!]: 0 }))
                        } catch (e: any) {
                          if (e && (e.status === 409 || /삭제할 수 없습니다|참조/.test(String(e.message)))) {
                            setRefBlockedMessage(String(e.message || '이 라벨셋을 참조하는 데이터셋 작업이 있어 삭제할 수 없습니다. 학습 데이터셋 관리에서 관련 작업을 먼저 정리해 주세요.'))
                            setRefBlockedOpen(true)
                          } else {
                            alert(e?.message || '삭제에 실패했습니다.')
                          }
                        } finally {
                          setDeletingLabelVersionId(null)
                        }
                      }}
                    >삭제 후 재업로드</button>
                  )}
                  {status.status === 'failed' && status.fail_reason !== 'unsupported_format' && (
                    <button
                      className={`px-3 py-1 text-sm border border-gray-300 rounded ${
                        deletingLabelVersionId === selected?.id
                          ? 'bg-gray-100 text-gray-400 cursor-wait'
                          : 'hover:bg-gray-50'
                      }`}
                      disabled={deletingLabelVersionId !== null}
                      onClick={async () => {
                        if (!selected) return
                        if (!window.confirm('실패한 라벨 세트를 삭제하시겠습니까?\n삭제 후 재업로드할 수 있습니다.')) return
                        try {
                          setDeletingLabelVersionId(selected.id)
                          await labelService.deleteLabelSet(selected.id)
                          setStatus(null)
                          setItems([])
                          setSources(null)
                          setMeta(null)
                          setLeafLabelCounts(prev => ({ ...prev, [selected.id!]: 0 }))
                        } catch (e: any) {
                          if (e && (e.status === 409 || /삭제할 수 없습니다|참조/.test(String(e.message)))) {
                            setRefBlockedMessage(String(e.message || '이 라벨셋을 참조하는 데이터셋 작업이 있어 삭제할 수 없습니다. 학습 데이터셋 관리에서 관련 작업을 먼저 정리해 주세요.'))
                            setRefBlockedOpen(true)
                          } else {
                            alert(e?.message || '삭제에 실패했습니다.')
                          }
                        } finally {
                          setDeletingLabelVersionId(null)
                        }
                      }}
                    >삭제 후 재업로드</button>
                  )}
                  {status.status === 'failed' && status.fail_reason === 'unsupported_format' && (
                    <button
                      className={`px-3 py-1 text-sm border border-red-300 text-red-700 rounded bg-white ${
                        deletingLabelVersionId === selected?.id ? 'cursor-wait opacity-75' : 'hover:bg-red-50'
                      }`}
                      disabled={deletingLabelVersionId !== null}
                      onClick={async () => {
                        if (!selected) return
                        try {
                          setDeletingLabelVersionId(selected.id)
                          await labelService.deleteLabelSet(selected.id)
                          // 상태 초기화 후 업로드 모달 오픈
                          setStatus(null)
                          setItems([])
                          setSources(null)
                          setMeta(null)
                          setLeafLabelCounts(prev => ({ ...prev, [selected.id!]: 0 }))
                          setUploadOpen(true)
                        } catch (e: any) {
                          alert(e?.message || '재업로드 준비 중 오류가 발생했습니다.')
                        } finally {
                          setDeletingLabelVersionId(null)
                        }
                      }}
                    >재업로드</button>
                  )}
                </div>
              )
            )}
          </div>

          {/* 설명 섹션 */}
          {selected && (
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center justify-start mb-1 gap-2">
                <div className="text-base font-semibold text-gray-900">설명</div>
                <button
                  type="button"
                  className="text-xs px-2 py-0.5 border border-gray-300 rounded bg-white text-gray-700 hover:bg-gray-100"
                  onClick={() => setDescExpanded(v => !v)}
                  aria-expanded={descExpanded}
                  aria-label={descExpanded ? '설명 접기' : '설명 더보기'}
                >
                  {descExpanded ? '접기' : '더보기'}
                </button>
              </div>
              <div className={`text-sm whitespace-pre-wrap break-all overflow-hidden leading-relaxed transition-all ${descExpanded ? '' : 'max-h-24 overflow-hidden'}`}>
                {selected.description && String(selected.description).trim().length > 0
                  ? <span className="text-gray-700">{selected.description}</span>
                  : <span className="text-gray-400 italic">입력한 내용이 없습니다</span>}
              </div>
            </div>
          )}

          {isVersionNode(selected) && (
            <div className="px-4 py-3 border-b border-gray-200 bg-white">
              <div className="text-sm font-semibold text-gray-900 mb-1">라벨셋 정보</div>
              {meta ? (
                <div className="text-xs text-gray-800 mb-2">
                  <div className="flex flex-wrap gap-x-4 gap-y-1">
                    <div><span className="text-gray-500">형식:</span> <span className="font-medium">{meta.format || '-'}</span></div>
                    <div><span className="text-gray-500">작업유형:</span> <span className="font-medium">{meta.task_type || '-'}</span></div>
                    <div><span className="text-gray-500">파일명:</span> <span className="font-medium break-all">{meta.archive_filename || '-'}</span></div>
                  </div>
                </div>
              ) : (
                <div className="text-xs text-gray-500 mb-2">표시할 라벨셋 정보가 없습니다</div>
              )}
              <div className="text-sm font-semibold text-gray-900 mb-1">선택한 원천데이터</div>
              {sources && sources.length > 0 ? (
                <ul className="space-y-1">
                  {sources.map(s => (
                    <li
                      key={`${s.raw_node_id}-${s.raw_batch_id ?? 'all'}`}
                      className="text-xs text-gray-800 flex flex-wrap items-center gap-1 max-w-full"
                    >
                      <span className="font-medium break-all max-w-full">
                        {(s.full_path_name && s.full_path_name.trim().length > 0)
                          ? s.full_path_name
                          : `Node#${s.raw_node_id}`}
                      </span>
                      <span className="mx-1 text-gray-500">&gt;</span>
                      <span className="break-all">
                        {s.raw_batch_id != null
                          ? (s.batch_display_name || `Batch#${s.raw_batch_id}`)
                          : '전체'}
                      </span>
                    </li>
                  ))}
                </ul>
              ) : (
                <div className="text-xs text-gray-500">표시할 원천데이터가 없습니다</div>
              )}
            </div>
          )}

          <div className="p-4 overflow-y-scroll" style={{ height: 'calc(100vh - 260px)' }}>
            {/* 선택된 노드가 폴더(리프 아님)이고 하위 노드가 있으면 우측에 하위 항목을 표시 */}
            {selected && selected.depth < 2 && (() => {
              const childNodes = nodes.filter(n => n.parent_id === selected.id)
              const hasChildren = childNodes.length > 0
              if (!hasChildren) return null
              return (
                <div>
                  <h4 className="font-medium text-gray-900 mb-3">하위 항목 ({childNodes.length}개)</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {childNodes.map(child => (
                      <div
                        key={child.id}
                        className="p-3 border border-gray-200 rounded-lg hover:border-gray-400 hover:bg-gray-50 cursor-pointer transition-all"
                        onClick={async () => { await handleNodeSelect(child) }}
                      >
                        <div className="flex items-center">
                          <svg className="w-5 h-5 text-gray-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-5l-2-2H5a2 2 0 00-2 2z" />
                          </svg>
                          <div>
                            <div className="font-medium text-gray-900">{child.name}</div>
                            <div className="text-xs text-gray-500">
                              {child.depth === 2
                                ? `라벨 ${leafLabelCounts[child.id] != null ? leafLabelCounts[child.id] : 0}`
                                : `하위 ${(() => {
                                    const cnt = childCounts[child.id]
                                    if (cnt != null) return cnt
                                    return nodes.filter(n => n.parent_id === child.id).length
                                  })()}개`}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )
            })()}

            {/* 리프(버전) 노드에서만 라벨 항목 탭/목록 표시 */}
            {isVersionNode(selected) && status?.status === 'completed' && (
              <div>
                <div className="flex items-center gap-3 mb-3">
                  <button className={`px-3 py-1 text-sm rounded ${tab === 'matched' ? 'bg-gray-800 text-white' : 'border border-gray-300'}`} onClick={async () => { setTab('matched'); setDeferList(false); setLabelSkip(0); setLabelHasMore(false); if (selected) await loadFirstItems(selected.id, 'matched') }}>일치({status.matched})</button>
                  <button className={`px-3 py-1 text-sm rounded ${tab === 'unmatched' ? 'bg-gray-800 text-white' : 'border border-gray-300'}`} onClick={async () => { setTab('unmatched'); setDeferList(false); setLabelSkip(0); setLabelHasMore(false); if (selected) await loadFirstItems(selected.id, 'unmatched') }}>불일치({status.unmatched})</button>
                  {/** 중복 탭 숨김 처리
                  <button className={`px-3 py-1 text-sm rounded ${tab === 'duplicate' ? 'bg-gray-800 text-white' : 'border border-gray-300'}`} onClick={async () => { setTab('duplicate'); setDeferList(false); setLabelSkip(0); setLabelHasMore(false); if (selected) await loadFirstItems(selected.id, 'duplicate') }}>중복({status.duplicates ?? 0})</button>
                  **/}
                </div>
                {(() => {
                  const displayed = items.filter(it => {
                    if (tab === 'matched') return it.match_status === 'matched'
                    if (tab === 'unmatched') return it.match_status === 'unmatched'
                    if (tab === 'duplicate') return it.match_status === 'duplicate'
                    return true
                  })
                  if (displayed.length === 0) {
                    return (
                      <div className="flex items-center justify-center py-12">
                        <div className="text-center">
                          <p className="text-lg font-semibold text-gray-800">목록이 없습니다.</p>
                        </div>
                      </div>
                    )
                  }
                  return (
                  <div className="space-y-2">
                    {displayed.map(it => (
                      <div key={it.id} className="p-3 border border-gray-200 rounded flex items-center justify-between">
                        {/* 라벨 항목: 긴 경로도 행 안에서만 잘리도록 min-w-0 / truncate 사용 */}
                        <div className="flex-1 min-w-0 text-sm text-gray-800 overflow-hidden">
                          <span className={`inline-block px-2 py-0.5 mr-2 rounded text-white text-xs ${it.match_status === 'matched' ? 'bg-green-600' : it.match_status === 'duplicate' ? 'bg-yellow-600' : 'bg-red-600'}`}>{it.match_status}</span>
                          <span className="inline-block max-w-full truncate align-middle" title={it.label_rel_path}>
                            {it.label_rel_path}
                          </span>
                        </div>
                        <div className="ml-4 text-xs text-gray-500 max-w-xs md:max-w-sm lg:max-w-md overflow-hidden">
                          {it.match_status === 'matched' ? (
                            <span className="inline-block max-w-full truncate align-middle" title={it.matched_raw_rel_path || undefined}>
                              원천: {it.matched_raw_rel_path}
                            </span>
                          ) : it.match_status === 'duplicate' ? (
                            '원천 후보 여러 개'
                          ) : (
                            '원천 없음'
                          )}
                        </div>
                      </div>
                    ))}
                      {(labelHasMore && displayed.length > 0) && (
                        <div className="mt-3 flex justify-center">
                          <button
                            className="px-3 py-1 text-sm border border-gray-300 rounded bg-white hover:bg-gray-50"
                            onClick={loadMoreItems}
                          >더보기</button>
                        </div>
                      )}
                    </div>
                  )
                })()}
              </div>
            )}
            {isVersionNode(selected) && (!status || status.status !== 'completed') && (
              <div className="flex flex-col items-center justify-center" style={{ height: '100%' }}>
                <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12.586 2.586A2 2 0 0 0 11.172 2H4a2 2 0 0 0-2 2v7.172a2 2 0 0 0 .586 1.414l8.704 8.704a2.426 2.426 0 0 0 3.42 0l6.58-6.58a2.426 2.426 0 0 0 0-3.42z" />
                  <circle cx="7.5" cy="7.5" r=".5" fill="currentColor" />
                </svg>
                <div className="text-center">
                  <p className="text-lg font-medium text-gray-900">업로드된 라벨데이터가 없습니다</p>
                  <p className="text-sm text-gray-600">라벨을 압축한 파일을 업로드 해주세요</p>
                </div>
              </div>
            )}
            {!selected && (
              <div className="flex items-center justify-center" style={{ minHeight: 'calc(100vh - 400px)' }}>
                <div className="text-center text-gray-500">
                  <svg className="mx-auto h-12 w-12 text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-5l-2-2H5a2 2 0 00-2 2z" />
                  </svg>
                  <p className="text-lg font-medium">노드를 선택하세요</p>
                  <p className="text-sm text-center">좌측의 라벨 데이터 구조에서 노드를 클릭하여 내용을 확인하세요.</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <CreateLabelDataModal
        isOpen={isCreateOpen}
        onClose={() => setCreateOpen(false)}
        onSubmit={handleCreate}
        canDirectProjectInput={canManageLabelProjectGlobal}
      />
      <LabelUploadModal
        isOpen={isUploadOpen}
        onClose={() => setUploadOpen(false)}
        onSubmit={async ({ selections, file, taskType }) => {
          if (!selected) return
          try {
            setUploading(true)
            // 이전 진행 상태가 잠깐 보이지 않도록 새 업로드 시작 전에 초기화
            setProgressStatus(null)
            // 진행 모달: 업로드 단계 표시
            setProgressOpen(true)
            setIngestStep('uploading')
            // 현재 백엔드가 단일 경로만 지원하므로 첫 번째 선택을 사용
            const primary = selections[0]
            const rawSources = selections.map(s => ({ raw_node_id: s.rawNodeId, raw_batch_id: s.rawBatchId ?? null }))
            await labelService.uploadLabelSet({ versionNodeId: selected.id, rawNodeId: primary.rawNodeId, rawBatchId: primary.rawBatchId ?? undefined, file, rawSources, taskType })
            // 업로드 완료 → 처리 단계로 전환 (폴링은 상단 useEffect에서 단일 수행)
            setIngestStep('processing')
            setStatus({ label_set_id: 0, status: 'processing', total: 0, matched: 0, unmatched: 0 })
            setUploadOpen(false)
          } catch (err: any) {
            setIngestStep('failed')
            setProgressStatus(null)
            try {
              const msg = (err && (err.message || err.toString())) || '업로드에 실패했습니다.'
              alert(msg)
            } catch {}
          } finally {
            setUploading(false)
          }
        }}
      />
      <LabelIngestProgressModal
        isOpen={isProgressOpen}
        onClose={() => setProgressOpen(false)}
        step={ingestStep}
        status={progressStatus}
      />
      <InfoModal
        isOpen={isRefBlockedOpen}
        title="삭제할 수 없습니다"
        message={refBlockedMessage || '이 라벨셋을 참조하는 데이터셋 작업이 있어 삭제할 수 없습니다. 학습 데이터셋 관리에서 관련 작업을 먼저 정리해 주세요.'}
        onClose={() => setRefBlockedOpen(false)}
        primaryText="확인"
      />
    </div>
  )
}


